import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Bell,
  Calendar,
  Check,
  ChevronDown,
  ClipboardList,
  Clock,
  Copy,
  Download,
  FileText,
  HeartPulse,
  History,
  LockKeyhole,
  Loader2,
  Mail,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  ExternalLink,
  Settings,
  SquarePen,
  User,
} from "lucide-react";
import { PatientNotificationPreferencesDialog } from "@/components/patient/PatientNotificationPreferencesDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HoverTooltip } from "@/components/ui/tooltip";
import { formatUsTimezoneLabel } from "@/constants/usOrgTimezones";
import { useGetData } from "@/hooks/useGetData";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { patientV2LoaderWrapper } from "../shared/PatientV2CircularLoader";
import AxiosInstance from "@/utils/AxiosInstance";
import {
  formatDate,
  formatDateTimeNew,
  formatPlanStatusLabel,
  formatUSPhoneNumber,
  getCurrentMonthYear,
  getErrToast,
  getPatientContextTimezoneDisplay,
  getPromise,
  getPromiseToast,
  getStatusBadge,
  splitIcdCode,
} from "@/utils/helpers";
import { toast } from "sonner";
import type { CarePlanV2, DeviceV2, PatientV2, TreatmentPlanV2 } from "../types";
import { patientV2SoftBadgeClass } from "../constants";
import { CriticalRecordsSection } from "./CriticalRecordsSection";
import { PatientEnrollmentHistoryDialog } from "./PatientEnrollmentHistoryDialog";
import { UpdateLastVisitDialog } from "./UpdateLastVisitDialog";
import { UpdatePatientPasswordDialog } from "./UpdatePatientPasswordDialog";
import { PatientNotificationPreferencesSection } from "./PatientNotificationPreferencesSection";
import { getPatientTimeZone } from "./patientHistoryHelpers";
import { downloadBackendPdf } from "@/utils/downloadBackendPdf";
import { getMonthYearOptions } from "./readingCountHelpers";

type Props = {
  patient: PatientV2 | null;
  patientId: string;
  carePlans: CarePlanV2[];
  treatmentPlans?: TreatmentPlanV2[];
  onCarePlanClick?: () => void;
  onTxPlanClick?: () => void;
  onPatientUpdate?: (patient: PatientV2) => void;
  canAssignCaretaker?: boolean;
  isCaretaker?: boolean;
  canEditPatient?: boolean;
  canAccessProfileActions?: boolean;
  canUpdatePatientPassword?: boolean;
  canManageSpecialInstructions?: boolean;
  devices?: DeviceV2[];
  readingCount?: number;
  readingCountLoading?: boolean;
  selectedReadingMonth?: string;
  onSelectedReadingMonthChange?: (month: string) => void;
};

type CaretakerOption = {
  _id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  type?: string;
};

function patientName(patient: PatientV2 | null , NoMiddleName = false): string {
  if (!patient) return "—";
  if(!NoMiddleName) {
  return (
    [patient.firstName, patient.middleName, patient.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "—"
  );}
  return (
    [patient.firstName, patient.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "—"
  );
}

function initials(name: string): string {
  const parts = name.split(" ").filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "PT";
}

function formatBannerDob(dob?: string): string {
  if (!dob) return "—";
  const raw = String(dob).split("T")[0];
  const parts = raw.split("-");
  if (parts.length === 3) return `${parts[1]}-${parts[2]}-${parts[0]}`;
  return raw;
}

function resolvePatientTimezoneDisplay(patient: PatientV2 | null): string {
  if (!patient) return "—";

  const contextTimezone = getPatientContextTimezoneDisplay(patient);
  if (contextTimezone && contextTimezone !== "—") return contextTimezone;

  const directTimezone = formatUsTimezoneLabel(patient.timezone);
  if (directTimezone && directTimezone !== "—") return directTimezone;

  const org =
    typeof patient.organizationId === "object" ? patient.organizationId : null;
  const facility =
    typeof patient.facilityId === "object" ? patient.facilityId : null;
  const orgTimezone = formatUsTimezoneLabel(org?.timezone ?? org?.timeZone);
  if (orgTimezone !== "—") return orgTimezone;

  const facilityTimezone = formatUsTimezoneLabel(facility?.timezone ?? facility?.timeZone);
  if (facilityTimezone !== "—") return facilityTimezone;

  return "—";
}

type AssignableClinician = {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  npiNumber?: string;
};

function resolveAssignableClinician(value: unknown): AssignableClinician | null {
  if (!value || typeof value !== "object") return null;
  return value as AssignableClinician;
}

function formatClinicianName(person: AssignableClinician | null): string {
  if (!person) return "—";
  return (
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ").trim() || "—"
  );
}

function recentPlan(carePlans: CarePlanV2[]): CarePlanV2 | null {
  if (!carePlans.length) return null;
  return [...carePlans].sort((a, b) =>
    String(b.updatedAt ?? b.createdAt ?? "").localeCompare(
      String(a.updatedAt ?? a.createdAt ?? ""),
    ),
  )[0];
}

function recentTreatmentPlan(treatmentPlans: TreatmentPlanV2[]): TreatmentPlanV2 | null {
  if (!treatmentPlans.length) return null;
  return [...treatmentPlans].sort((a, b) =>
    String(b.updatedAt ?? b.createdAt ?? "").localeCompare(
      String(a.updatedAt ?? a.createdAt ?? ""),
    ),
  )[0];
}

function diagnosisEntries(patient: PatientV2 | null): string[] {
  if (!patient) return [];
  const p = patient as Record<string, unknown>;
  return Object.keys(p)
    .filter((key) => /^dx\d+$/i.test(key) && String(p[key] ?? "").trim() !== "")
    .sort(
      (a, b) =>
        parseInt(a.replace(/\D/g, ""), 10) - parseInt(b.replace(/\D/g, ""), 10),
    )
    .map((key) => String(p[key]).trim());
}

function DiagnosisChip({ line }: { line: string }) {
  const raw = String(line).trim();
  const [code, description] = splitIcdCode(raw);
  const codeLabel = code || raw;
  const tooltipContent = description ? (
    <div className="max-w-xs text-sm leading-snug">
      <span className="font-semibold">{codeLabel}</span>
      <span className="text-muted-foreground"> — </span>
      <span>{description}</span>
    </div>
  ) : (
    <span className="text-sm font-semibold">{codeLabel}</span>
  );

  return (
    <HoverTooltip content={tooltipContent} side="top" >
      <div className="inline-flex cursor-pointer max-w-full min-w-0 items-center rounded-full border border-[#E5E7EB] bg-[#F9FAFB] py-0.5 pl-0.5 pr-2">
        <span className="shrink-0 rounded-full bg-[#E5E7EB] px-2 py-0.5 text-xs font-semibold text-[#111827]">
          {codeLabel}
        </span>
        {description ? (
          <span className="ml-1.5 min-w-0 break-words max-w-44 text-xs font-medium text-[#111827] sm:max-w-56">
            {description}
          </span>
        ) : null}
      </div>
    </HoverTooltip>
  );
}

const toolbarIconButtonClass =
  "h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground";

const contactToolbarButtonClass =
  "h-8 w-8 cursor-pointer rounded-full border border-grey bg-[#ffffff] p-0 text-[#8E8E93] hover:bg-grey hover:text-[#1F2937]";

function PatientSummaryPhonePopover({
  patient,
  copiedPhone,
  onCopyPhone,
}: {
  patient: PatientV2 | null;
  copiedPhone: Record<string, boolean>;
  onCopyPhone: (text: string, key: string) => void;
}) {
  const formatPhoneCopyValue = (phone?: string, extension?: string) => {
    const formattedPhone = formatUSPhoneNumber(String(phone ?? ""));
    return extension ? `${formattedPhone} ext. ${extension}` : formattedPhone;
  };

  const toAircallContactUrl = (phone?: string | null) => {
    const digits = String(phone ?? "").replace(/\D/g, "");
    if (!digits) return null;
    return `https://workspace.aircall.io/contacts/${digits}`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          className={contactToolbarButtonClass}
          aria-label="Phone numbers"
        >
          <Phone className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="end">
        <div className="space-y-2">
          <div className="text-sm font-semibold">Phone Numbers</div>
          {patient?.primaryNumber ? (
            <div className="space-y-1">
              <div className="text-muted-foreground text-xs">Primary</div>
              <div className="flex items-center gap-2">
                <a
                  href={`tel:${patient.primaryNumber}`}
                  className="text-primary flex flex-1 items-center gap-2 text-sm hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {formatUSPhoneNumber(patient.primaryNumber)}
                </a>
                {toAircallContactUrl(patient.primaryNumber) ? (
                  <a
                    href={toAircallContactUrl(patient.primaryNumber)!}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-primary hover:bg-muted"
                    title="Open in Aircall"
                    aria-label="Open in Aircall"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() =>
                    onCopyPhone(formatPhoneCopyValue(patient.primaryNumber), "primary")
                  }
                  title="Copy phone number"
                >
                  {copiedPhone.primary ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          ) : null}
          {patient?.secondaryNumber ? (
            <div className="space-y-1">
              <div className="text-muted-foreground text-xs">Secondary</div>
              <div className="flex items-center gap-2">
                <a
                  href={`tel:${patient.secondaryNumber}`}
                  className="text-primary flex flex-1 items-center gap-2 text-sm hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {formatUSPhoneNumber(patient.secondaryNumber)}
                </a>
                {toAircallContactUrl(patient.secondaryNumber) ? (
                  <a
                    href={toAircallContactUrl(patient.secondaryNumber)!}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-primary hover:bg-muted"
                    title="Open in Aircall"
                    aria-label="Open in Aircall"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() =>
                    onCopyPhone(formatPhoneCopyValue(patient.secondaryNumber), "secondary")
                  }
                  title="Copy phone number"
                >
                  {copiedPhone.secondary ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          ) : null}
          {patient?.landlineNumber ? (
            <div className="space-y-1">
              <div className="text-muted-foreground text-xs">Landline</div>
              <div className="flex items-center gap-2">
                <a
                  href={`tel:${patient.landlineNumber}`}
                  className="text-primary flex flex-1 items-center gap-2 text-sm hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {formatUSPhoneNumber(patient.landlineNumber)}
                  {patient?.landlineExtension ? ` ext. ${patient.landlineExtension}` : ""}
                </a>
                {toAircallContactUrl(patient.landlineNumber) ? (
                  <a
                    href={toAircallContactUrl(patient.landlineNumber)!}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-primary hover:bg-muted"
                    title="Open in Aircall"
                    aria-label="Open in Aircall"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() =>
                    onCopyPhone(
                      formatPhoneCopyValue(
                        patient.landlineNumber,
                        patient.landlineExtension,
                      ),
                      "landline",
                    )
                  }
                  title="Copy phone number"
                >
                  {copiedPhone.landline ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          ) : null}
          {!patient?.primaryNumber &&
          !patient?.secondaryNumber &&
          !patient?.landlineNumber &&
          !patient?.phoneNumber ? (
            <div className="text-muted-foreground text-sm">No phone numbers available</div>
          ) : null}
          {!patient?.primaryNumber &&
          !patient?.secondaryNumber &&
          !patient?.landlineNumber &&
          patient?.phoneNumber ? (
            <div className="space-y-1">
              <div className="text-muted-foreground text-xs">Phone</div>
              <div className="flex items-center gap-2">
                <a
                  href={`tel:${patient.phoneNumber}`}
                  className="text-primary flex flex-1 items-center gap-2 text-sm hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {formatUSPhoneNumber(patient.phoneNumber)}
                </a>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() =>
                    onCopyPhone(formatPhoneCopyValue(patient.phoneNumber), "phone")
                  }
                  title="Copy phone number"
                >
                  {copiedPhone.phone ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PatientSummaryEmailPopover({
  patient,
  copiedEmail,
  onCopyEmail,
}: {
  patient: PatientV2 | null;
  copiedEmail: boolean;
  onCopyEmail: (email: string) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          className={contactToolbarButtonClass}
          aria-label="Email address"
        >
          <Mail className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="end">
        <div className="space-y-2">
          <div className="text-sm font-semibold">Email Address</div>
          {patient?.email ? (
            <div className="flex items-center gap-2">
              <a
                href={`mailto:${patient.email}`}
                className="text-primary flex flex-1 items-center gap-2 text-sm hover:underline"
              >
                <Mail className="h-3 w-3" />
                {patient.email}
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onCopyEmail(patient.email!)}
                title="Copy email"
              >
                {copiedEmail ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">No email address available</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

 function PatientSummary({
  patient,
  patientId,
  carePlans,
  treatmentPlans = [],
  onCarePlanClick,
  onTxPlanClick,
  onPatientUpdate,
  canAssignCaretaker = true,
  isCaretaker = false,
  canEditPatient = false,
  canAccessProfileActions = false,
  canUpdatePatientPassword = false,
  canManageSpecialInstructions = false,
  readingCount = 0,
  readingCountLoading = false,
  selectedReadingMonth: selectedReadingMonthProp,
  onSelectedReadingMonthChange,
}: Props) {
  console.log("Patient Tab 1 -------------------------------------------------------------------------------");
  const navigate = useNavigate();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isEnrollmentHistoryOpen, setIsEnrollmentHistoryOpen] = useState(false);
  const [patientNotifDialogOpen, setPatientNotifDialogOpen] = useState(false);
  const [isUpdatePasswordOpen, setIsUpdatePasswordOpen] = useState(false);
  const showProfileActionsMenu = canAccessProfileActions || canUpdatePatientPassword;
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [caretakersOpen, setCaretakersOpen] = useState(true);
  const [medicalOpen, setMedicalOpen] = useState(true);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState<Record<string, boolean>>({});
  const [isAssignCaretakerDialogOpen, setIsAssignCaretakerDialogOpen] = useState(false);
  const [isLastVisitDialogOpen, setIsLastVisitDialogOpen] = useState(false);
  const [selectedCaretakerIds, setSelectedCaretakerIds] = useState<string[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const organizationId =
    typeof patient?.organizationId === "object"
      ? patient?.organizationId?._id
      : patient?.organizationId;
  

  // have to implement paginated scroll/load more caretakers as per requirement.
  const {
    apiData: caretakersApiData,
    loading: caretakersLoading,
    err: caretakerErr,
  } = useGetData("/caretakers", { orgId: organizationId , limit: 200 }, false, !!organizationId);

  const allCaretakers = (caretakersApiData as { data?: { caretakers?: CaretakerOption[] } } | null)
    ?.data?.caretakers ?? [];
console.log('allCaretakers -->' , allCaretakers)
  const availableCaretakers = useMemo(() => {
    if (!allCaretakers.length) return [];

    const patientType = patient?.typeOfPatient;
    if (patientType === "facility") {
      return allCaretakers.filter((caretaker) => caretaker.type === "facility");
    }

    return allCaretakers.filter(
      (caretaker) => caretaker.type === "no_facility" || caretaker.type === "all",
    );
  }, [allCaretakers, patient?.typeOfPatient]);

  const assignedCaretakers = useMemo(
    () =>
      (patient?.assignedCaretakerIds ?? []).filter(
        (caretaker): caretaker is Exclude<typeof caretaker, string> =>
          typeof caretaker === "object" && caretaker !== null,
      ),
    [patient?.assignedCaretakerIds],
  );

  const latestCarePlan = recentPlan(carePlans);
  const latestTxPlan = recentTreatmentPlan(treatmentPlans);
  const name = patientName(patient,false); // true means no middle name
  const statusBadge = getStatusBadge(patient?.status);
  const typeOfPatient = String(patient?.typeOfPatient ?? "");
  const isFacilityPatient = typeOfPatient.toLowerCase() === "facility";
  const patientPrograms = patient?.programs ?? [];
  const programsLabel = patientPrograms
    .map((program) => String(program).toUpperCase())
    .join(" , ");
  const timezoneLabel = resolvePatientTimezoneDisplay(patient);
  const carePlanStatusRaw = String(
    (patient as Record<string, unknown>)?.lastCarePlanStatus ?? latestCarePlan?.status ?? "",
  ).trim();
  const txPlanStatusRaw = String(
    (patient as Record<string, unknown>)?.lastTreatmentPlanStatus ?? latestTxPlan?.status ?? "",
  ).trim();
  const carePlanBadge = getStatusBadge(carePlanStatusRaw);
  const txPlanBadge = getStatusBadge(txPlanStatusRaw);
  const carePlanStatusLabel =
    carePlanStatusRaw === "followUp"
      ? "Pending Follow Up"
      : formatPlanStatusLabel(carePlanStatusRaw);
  const txPlanStatusLabel = formatPlanStatusLabel(txPlanStatusRaw);
  const diagnoses = useMemo(() => diagnosisEntries(patient), [patient]);
  const profilePdfMonth = selectedReadingMonthProp ?? getCurrentMonthYear();
  const patientTimeZone = useMemo(
    () => getPatientTimeZone(patient as Record<string, unknown> | null),
    [patient],
  );
  const patientTimezoneDisplay = useMemo(
    () => getPatientContextTimezoneDisplay(patient),
    [patient],
  );

  const patientDobRaw = String(patient?.dob ?? patient?.dateOfBirth ?? "").trim();
  const phone =
    patient?.primaryNumber ?? patient?.phoneNumber
      ? formatUSPhoneNumber(String(patient?.primaryNumber ?? patient?.phoneNumber))
      : "—";

  const patientDobDisplay = patientDobRaw
    ? formatDate(patientDobRaw) || formatBannerDob(patientDobRaw)
    : "—";
  const allergiesDisplay =
    String(patient?.allergies ?? "").trim() || "No allergies recorded";
  const primaryInsuranceName = String(patient?.primaryInsuranceName ?? "").trim();
  const primaryInsurance = String(patient?.primaryInsuranceNumber ?? "").trim();
  const secondaryInsuranceName = String(patient?.secondaryInsuranceName ?? "").trim();
  const secondaryInsurance = String(patient?.secondaryInsuranceNumber ?? "").trim();
  const facilityName =
    typeof patient?.facilityId === "object"
      ? patient?.facilityId?.name ?? "—"
      : String(patient?.facilityId ?? "—");
  const facilityRoom = String(patient?.facilityRoom ?? "").trim();
  const showFacilityFields = isFacilityPatient || !!patient?.facilityId;
  const lastVisitRaw = patient?.lastVisit;
  const lastVisitCardDisplay =
    lastVisitRaw && String(lastVisitRaw).trim()
      ? formatDateTimeNew(String(lastVisitRaw))?.split(" ")?.[0] ?? "—"
      : "No last visit added";
  const selectedReadingMonth = selectedReadingMonthProp ?? getCurrentMonthYear();
  const readingMonthLabel =
    getMonthYearOptions().find((option) => option.value === selectedReadingMonth)?.label ??
    selectedReadingMonth;
  const assignedProvider = resolveAssignableClinician(patient?.assignedProviderId);
  const assignedPractitioner = resolveAssignableClinician(
    (patient as Record<string, unknown>)?.assignedPractitionerId,
  );
  const primaryContactPerson = String(
    (patient as Record<string, unknown>)?.primaryContactPerson ?? "",
  );

  const patientDetails: Array<{ label: string; value: string }> = [
    { label: "DOB", value: patientDobDisplay },
    { label: "Gender", value: patient?.gender ?? "—" },
    { label: "Phone", value: phone },
    { label: "Email", value: patient?.email ?? "—" },
    {
      label: "Address",
      value: (() => {
        const facility =
          typeof patient?.facilityId === "object" && patient?.facilityId
            ? patient.facilityId
            : null;
        const facilityAddress = facility
          ? [
              facility?.street,
              facility?.city,
              facility?.state,
              facility?.zipCode ?? (facility as { zipcode?: string })?.zipcode,
            ]
              .filter(Boolean)
              .join(", ")
          : "";
        const patientAddress = [
          patient?.address,
          patient?.street,
          patient?.city,
          patient?.state,
          patient?.zipCode,
        ]
          .filter(Boolean)
          .join(", ");
        if (isFacilityPatient) {
          return facilityAddress || "—";
        }
        return patientAddress || "—";
      })(),
    },
    {
      label: "Organization",
      value:
        typeof patient?.organizationId === "object"
          ? patient?.organizationId?.organizationName ?? "—"
          : String(patient?.organizationId ?? "—"),
    },
    ...(showFacilityFields
      ? [
          { label: "Facility", value: facilityName },
          { label: "Room", value: facilityRoom || "—" },
        ]
      : []),
  ];

  const formatInsuranceDisplay = (name: string, number: string) => {
    if (name && number) return `${name} — ${number}`;
    if (name) return name;
    if (number) return number;
    return "—";
  };

  const insuranceDetails: Array<{ label: string; value: string }> = [
    {
      label: "Primary Insurance",
      value: formatInsuranceDisplay(primaryInsuranceName, primaryInsurance),
    },
    {
      label: "Secondary Insurance",
      value: formatInsuranceDisplay(secondaryInsuranceName, secondaryInsurance),
    },
  ];

  const hasCareTeam = !!(assignedProvider || assignedPractitioner);
  const hasInsurance = insuranceDetails.length > 0;

  const renderDetailRow = (
    row: { label: string; value: string },
    showBorder: boolean,
  ) => (
    <div
      key={row.label}
      className={`py-2.5 ${showBorder ? "border-b border-dashed border-border/70" : ""}`}
    >
      <p className="text-xs text-muted-foreground">{row.label}</p>
      <p className="wrap-break-word text-sm font-semibold text-[#111827]">{row.value}</p>
    </div>
  );

  const renderCareTeamMember = ({
    label,
    person,
    isPrimary,
    showBorder,
  }: {
    label: string;
    person: AssignableClinician;
    isPrimary: boolean;
    showBorder: boolean;
  }) => (
    <div
      className={`py-2.5 ${showBorder ? "border-b border-dashed border-border/70" : ""}`}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <p className="wrap-break-word text-sm font-semibold text-[#111827]">
          {formatClinicianName(person)}
        </p>
        {isPrimary ? (
          <span className="rounded-full border border-secondary px-3 py-0.5 text-xs text-secondary">
            Primary
          </span>
        ) : null}
      </div>
      {person.npiNumber ? (
        <p className="mt-0.5 text-xs text-muted-foreground">NPI Number: {person.npiNumber}</p>
      ) : null}
    </div>
  );

  const copyToClipboard = async (text: string, type: "email" | "phone", key?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "email") {
        setCopiedEmail(true);
        window.setTimeout(() => setCopiedEmail(false), 2000);
      } else if (type === "phone" && key) {
        setCopiedPhone((prev) => ({ ...prev, [key]: true }));
        window.setTimeout(() => {
          setCopiedPhone((prev) => ({ ...prev, [key]: false }));
        }, 2000);
      }
      toast.success(`${type === "email" ? "Email" : "Phone number"} copied to clipboard`);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleDownloadPatientPdf = async () => {
    const id = patient?._id || patientId;
    if (!id) {
      toast.error("Patient data is not loaded yet.");
      return;
    }

    setIsDownloadingPdf(true);
    try {
      await downloadBackendPdf({
        url: `/patients/${id}/profile-pdf`,
        params: { month: profilePdfMonth },
        fallbackFilename: "patient-profile.pdf",
      });
      toast.success("Profile PDF downloaded");
    } catch (err) {
      getErrToast(err);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const openAssignCaretakerDialog = () => {
    if (isCaretaker) return;
    setSelectedCaretakerIds(
      (patient?.assignedCaretakerIds ?? [])
        .map((caretaker) => (typeof caretaker === "string" ? caretaker : caretaker?._id))
        .filter((id): id is string => !!id),
    );
    setIsAssignCaretakerDialogOpen(true);
  };

  const toggleCaretaker = (caretakerId: string) => {
    setSelectedCaretakerIds((prev) =>
      prev.includes(caretakerId)
        ? prev.filter((id) => id !== caretakerId)
        : [...prev, caretakerId],
    );
  };

  const handleAssignCaretakers = async () => {
    if (isCaretaker) {
      toast.error("You do not have permission to assign caretakers");
      return;
    }
    if (!patientId || !patient) return;

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      setAssignLoading(true);
      await AxiosInstance().put(`/patients/${patientId}`, {
        assignedCaretakerIds: selectedCaretakerIds,
      });
      onPatientUpdate?.({
        ...patient,
        assignedCaretakerIds: selectedCaretakerIds.map(
          (caretakerId) =>
            availableCaretakers.find((caretaker) => caretaker._id === caretakerId) ?? caretakerId,
        ),
      });
      resolveFn("Caretakers assigned successfully");
      setIsAssignCaretakerDialogOpen(false);
    } catch (error) {
      rejectFn(error);
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <>
    <Card className="overflow-hidden rounded-none border-0 px-0 py-0 shadow-none">
      <CardContent className="teachers p-0">
        <section aria-label="patient-summary-toolbar" className="border-b border-border px-3 py-2.5">
          <div className="flex items-center justify-end gap-2">
            {/* <div className="flex min-w-0 items-center gap-1.5">
              {carePlanStatusRaw ? (
                <HoverTooltip
                  content={
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Care Plan</span>
                      <span
                        className="rounded-full border px-2 py-0.5 text-[11px] font-medium"
                        style={{
                          backgroundColor: carePlanBadge.bgColor,
                          color: carePlanBadge.mainColor,
                          borderColor: carePlanBadge.mainColor,
                        }}
                      >
                        {carePlanStatusLabel}
                      </span>
                    </div>
                  }
                  className="bg-gray-200 text-black"
                  arrowColor="bg-gray-200 fill-gray-200"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={onCarePlanClick}
                    className="relative h-8 w-8 rounded-md border-amber-400/70 bg-amber-50 p-0 text-amber-700 hover:bg-amber-100"
                    aria-label={`Care Plan status: ${carePlanStatusLabel}`}
                  >
                    <FileText className="h-4 w-4" />
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white"
                      style={{ backgroundColor: carePlanBadge.mainColor }}
                      aria-hidden
                    />
                  </Button>
                </HoverTooltip>
              ) : null}
              {txPlanStatusRaw ? (
                <HoverTooltip
                  content={
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Tx Plan</span>
                      <span
                        className="rounded-full border px-2 py-0.5 text-[11px] font-medium"
                        style={{
                          backgroundColor: txPlanBadge.bgColor,
                          color: txPlanBadge.mainColor,
                          borderColor: txPlanBadge.mainColor,
                        }}
                      >
                        {txPlanStatusLabel}
                      </span>
                    </div>
                  }
                  className="bg-gray-200 text-black"
                  arrowColor="bg-gray-200 fill-gray-200"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={onTxPlanClick}
                    className="relative h-8 w-8 rounded-md border-violet-400/70 bg-violet-50 p-0 text-violet-700 hover:bg-violet-100"
                    aria-label={`Tx Plan status: ${txPlanStatusLabel}`}
                  >
                    <ClipboardList className="h-4 w-4" />
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white"
                      style={{ backgroundColor: txPlanBadge.mainColor }}
                      aria-hidden
                    />
                  </Button>
                </HoverTooltip>
              ) : null}
            </div> */}
            <div className="flex shrink-0 items-center gap-1">
              <div className="flex items-center gap-1">
                <PatientSummaryPhonePopover
                  patient={patient}
                  copiedPhone={copiedPhone}
                  onCopyPhone={(text, key) => void copyToClipboard(text, "phone", key)}
                />
                <PatientSummaryEmailPopover
                  patient={patient}
                  copiedEmail={copiedEmail}
                  onCopyEmail={(email) => void copyToClipboard(email, "email")}
                />
              </div>
              <div className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />
              <div className="flex items-center gap-0">
                {canEditPatient ? (
                <HoverTooltip
                  content="Edit Patient"
                  className="bg-gray-200 text-black"
                  arrowColor="bg-gray-200 fill-gray-200"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => navigate(`/patients/${patient?._id || patientId}/edit`)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="Edit patient"
                  >
                    <SquarePen className="h-4 w-4" />
                  </Button>
                </HoverTooltip>
              ) : null}
              <HoverTooltip
                content="Download Profile PDF"
                className="bg-gray-200 text-black"
                arrowColor="bg-gray-200 fill-gray-200"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => void handleDownloadPatientPdf()}
                  disabled={isDownloadingPdf || !patient}
                  className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Download profile PDF"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </HoverTooltip>
              {showProfileActionsMenu ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuLabel>Profile actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {canUpdatePatientPassword ? (
                      <DropdownMenuItem onClick={() => setIsUpdatePasswordOpen(true)}>
                        <LockKeyhole className="h-4 w-4" />
                        Update password
                      </DropdownMenuItem>
                    ) : null}
                    {canAccessProfileActions ? (
                      <>
                        <DropdownMenuItem onClick={() => setPatientNotifDialogOpen(true)}>
                          <Bell className="h-4 w-4" />
                          Notification settings
                        </DropdownMenuItem>
                        {/* <DropdownMenuItem onClick={() => setIsEnrollmentHistoryOpen(true)}>
                          <History className="h-4 w-4" />
                          Enrollment &amp; creation history
                        </DropdownMenuItem> */}
                        <DropdownMenuItem
                          onClick={() =>
                            navigate(`/patients/${patient?._id || patientId}/device-settings`)
                          }
                        >
                          <Settings className="h-4 w-4" />
                          Device settings
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              </div>
            </div>
          </div>
        </section>

        <section aria-label="patient-summary-overview" className="px-3.5 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative shrink-0">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-bold text-xl shadow-lg"
              >
                {initials(name)}
              </div>
            </div>
            <div className="min-w-0 flex flex-col items-start justify-center gap-1">
              <p className="truncate text-lg font-bold text-[#111827] capitalize">{name}</p>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{timezoneLabel}</span>
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
              style={{
                backgroundColor: statusBadge.bgColor,
                color: statusBadge.mainColor,
                borderColor: statusBadge.mainColor,
              }}
            >
              {(patient?.status || "").split("_").join(" ")}
            </Badge>

            <Badge
              variant="outline"
              className="rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
              style={{
                backgroundColor: "#EAB30826",
                color: "#A16207",
                borderColor: "#CA8A04",
              }}
            >
              {typeOfPatient}
            </Badge>

            {programsLabel ? (
              <Badge
                variant="outline"
                className="rounded-full px-2.5 py-0.5 text-[11px] font-medium uppercase"
                style={{
                  backgroundColor: "#0A5EE626",
                  color: "#0A5EE6",
                  borderColor: "#0A5EE6",
                }}
              >
                {programsLabel}
              </Badge>
            ) : null}
            <CriticalRecordsSection patient={patient} />
          </div>
        </section>

        <Separator />

        <div className="px-4 py-3">
          <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-dashed border-border/80 p-3">
              <p className="text-xs text-muted-foreground">Last Visit</p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="min-w-0 truncate text-sm font-semibold text-[#111827]">
                  {lastVisitCardDisplay}
                </p>
                {canEditPatient ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="h-8 w-8 shrink-0 rounded-md bg-[#E8F0FE] p-0 hover:bg-[#DDE7FC]"
                    onClick={() => setIsLastVisitDialogOpen(true)}
                    title="Edit Last Visit"
                    aria-label="Edit last visit date"
                  >
                    <Pencil className="h-4 w-4 text-[#2563EB]" />
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-border/80 p-3">
              <p className="text-xs text-muted-foreground">Reading Count</p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="flex min-h-5 items-center text-sm font-semibold text-[#111827]">
                  {readingCountLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
                  ) : (
                    readingCount
                  )}
                </p>
                {onSelectedReadingMonthChange ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        className="h-8 w-8 shrink-0 rounded-md bg-[#E8F0FE] p-0 hover:bg-[#DDE7FC]"
                        title="Select month"
                        aria-label="Select reading count month"
                      >
                        <Calendar className="h-4 w-4 text-[#2563EB]" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="end">
                      <div className="flex flex-col gap-3">
                        <div className="text-sm font-semibold">Select Month</div>
                        <Select
                          value={selectedReadingMonth}
                          onValueChange={onSelectedReadingMonthChange}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue>{readingMonthLabel}</SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px]">
                            {getMonthYearOptions().map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setDetailsOpen((prev) => !prev)}
          className="flex w-full items-center gap-2 px-4 py-3 text-left"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              detailsOpen ? "rotate-0" : "-rotate-90"
            }`}
          />
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-semibold">Patient Details</span>
        </button>
        {detailsOpen ? (
          <div className="px-4 pb-3">
            {patientDetails.map((row, idx) =>
              renderDetailRow(
                row,
                idx < patientDetails.length - 1 || hasCareTeam || hasInsurance,
              ),
            )}
            {assignedProvider
              ? renderCareTeamMember({
                  label: "Provider",
                  person: assignedProvider,
                  isPrimary: primaryContactPerson === "Provider",
                  showBorder: !!assignedPractitioner || hasInsurance,
                })
              : null}
            {assignedPractitioner
              ? renderCareTeamMember({
                  label: "Practitioner",
                  person: assignedPractitioner,
                  isPrimary: primaryContactPerson === "Practitioner",
                  showBorder: hasInsurance,
                })
              : null}
            {insuranceDetails.map((row, idx) =>
              renderDetailRow(row, idx < insuranceDetails.length - 1),
            )}
          </div>
        ) : null}

        <Separator />

        <button
          type="button"
          onClick={() => setMedicalOpen((prev) => !prev)}
          className="flex w-full items-center gap-2 px-4 py-3 text-left"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              medicalOpen ? "rotate-0" : "-rotate-90"
            }`}
          />
          <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-semibold">Medical Records</span>
        </button>
        {medicalOpen ? (
          <div className="px-4 pb-3">
            <div className="border-b border-dashed border-border/70 py-2.5">
              <p className="text-xs text-muted-foreground">Allergies</p>
              <p className="wrap-break-word text-sm font-semibold text-[#111827]">
                {allergiesDisplay}
              </p>
            </div>
            <div className="py-2.5">
              <p className="text-xs text-muted-foreground">Diagnostic List</p>
              {diagnoses.length === 0 ? (
                <p className="text-sm text-muted-foreground">No diagnoses recorded</p>
              ) : (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {diagnoses.map((dx, idx) => (
                    <DiagnosisChip key={`${dx}-${idx}`} line={dx} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <Separator />

        <div className="flex w-full items-center gap-2 px-4 py-3">
          <button
            type="button"
            onClick={() => setCaretakersOpen((prev) => !prev)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                caretakersOpen ? "rotate-0" : "-rotate-90"
              }`}
            />
            <HeartPulse className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-semibold">Caretakers</span>
            {assignedCaretakers.length > 0 ? (
              <Badge className={`${patientV2SoftBadgeClass} h-5 px-1.5 text-[10px] font-medium`}>
                {assignedCaretakers.length}
              </Badge>
            ) : null}
          </button>
          {canAssignCaretaker && !isCaretaker ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 rounded-full bg-muted/60"
              onClick={openAssignCaretakerDialog}
              aria-label="Assign caretakers"
            >
              <Plus className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        {caretakersOpen ? (
          <div className="px-4 pb-4">
            {assignedCaretakers.length > 0 ? (
              assignedCaretakers.map((caretaker, idx) => (
                <div
                  key={caretaker._id || caretaker.id || idx}
                  className={`py-2.5 ${
                    idx < assignedCaretakers.length - 1
                      ? "border-b border-dashed border-border/70"
                      : ""
                  }`}
                >
                  <p className="text-sm font-semibold text-[#111827]">
                    {[caretaker.firstName, caretaker?.middleName, caretaker.lastName].filter(Boolean).join(" ")}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {caretaker.email ?? "—"}
                  </p>
                </div>
              ))
            ) : (
              <p className="py-1 text-sm text-muted-foreground">No caretakers assigned</p>
            )}
          </div>
        ) : null}

        {canAccessProfileActions ? (
          <>
            <Separator />
            <PatientNotificationPreferencesSection patientId={patient?._id || patientId} />
          </>
        ) : null}
      </CardContent>

      <Dialog
        open={isAssignCaretakerDialogOpen && !isCaretaker}
        onOpenChange={(open) => {
          if (isCaretaker) {
            setIsAssignCaretakerDialogOpen(false);
            return;
          }
          setIsAssignCaretakerDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Caretakers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              Select caretakers to assign to this patient
            </p>
            <LoaderWrapper loading={caretakersLoading} error={caretakerErr} customLoaderComponent={patientV2LoaderWrapper()}>
              <div className="max-h-[400px] space-y-3 overflow-y-auto">
                {availableCaretakers.length > 0 ? (
                  availableCaretakers.map((caretaker) => (
                    <div
                      key={caretaker._id}
                      className="hover:bg-muted/50 flex cursor-pointer items-center space-x-3 rounded-lg border p-3"
                      onClick={() => toggleCaretaker(caretaker._id)}
                    >
                      <Checkbox
                        checked={selectedCaretakerIds.includes(caretaker._id)}
                        onCheckedChange={() => toggleCaretaker(caretaker._id)}
                        onClick={(e) => e.preventDefault()}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {caretaker.firstName} {caretaker?.middleName ? caretaker.middleName : ''} {caretaker.lastName}
                        </p>
                        <p className="text-muted-foreground text-xs">{caretaker.email}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    No caretakers available
                  </p>
                )}
              </div>
            </LoaderWrapper>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAssignCaretakerDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button disabled={assignLoading} onClick={() => void handleAssignCaretakers()}>
                Assign Caretakers
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>

      <PatientEnrollmentHistoryDialog
        patient={patient}
        open={isEnrollmentHistoryOpen}
        onOpenChange={setIsEnrollmentHistoryOpen}
      />
      <PatientNotificationPreferencesDialog
        patientId={patientId}
        open={patientNotifDialogOpen}
        onOpenChange={setPatientNotifDialogOpen}
        patientTimeZone={patientTimeZone}
        patientTimezoneDisplay={patientTimezoneDisplay}
      />
      <UpdatePatientPasswordDialog
        patientId={patient?._id || patientId}
        open={isUpdatePasswordOpen}
        onOpenChange={setIsUpdatePasswordOpen}
      />
      <UpdateLastVisitDialog
        patientId={patient?._id || patientId}
        lastVisit={lastVisitRaw ? String(lastVisitRaw) : ""}
        open={isLastVisitDialogOpen}
        onOpenChange={setIsLastVisitDialogOpen}
        onSuccess={(updatedLastVisit) => {
          if (patient && onPatientUpdate) {
            onPatientUpdate({ ...patient, lastVisit: updatedLastVisit });
          }
        }}
      />
    </>
  );
}

export const PatientSummaryMemo = React.memo(PatientSummary);
